import { User, Zap, Trophy, Medal, Award } from 'lucide-react';
import { formatUserName } from '../../../utils/export';

export interface UserRankingData {
  name: string;
  userId: string;
  sessions: number;
  totalMinutes: number;
  avgDuration: number;
  hasSensorData: number;
  rank: number;
}

interface UserRankingTableProps {
  data: UserRankingData[];
  title?: string;
  subtitle?: string;
  emptyMessage?: string;
  emptyDescription?: string;
  showSensorData?: boolean;
  className?: string;
}

function RankIcon({ rank }: { rank: number }) {
  if (rank === 1) return <Trophy className="h-5 w-5" style={{ color: 'var(--anl-warning)' }} />;
  if (rank === 2) return <Medal className="h-5 w-5" style={{ color: 'var(--anl-text-muted)' }} />;
  if (rank === 3) return <Award className="h-5 w-5" style={{ color: 'var(--anl-warning)' }} />;
  return (
    <span className="flex h-5 w-5 items-center justify-center text-xs font-bold text-[var(--anl-text-muted)]">
      #{rank}
    </span>
  );
}

function UserRankingRow({
  user,
  showSensorData = true,
}: {
  user: UserRankingData;
  showSensorData?: boolean;
}) {
  const getRankBg = (rank: number) => {
    if (rank === 1) return 'bg-[var(--anl-surface-elev)] border-[var(--anl-warning)]/40';
    if (rank === 2) return 'bg-[var(--anl-surface-elev)] border-[var(--anl-border)]/30';
    if (rank === 3) return 'bg-[var(--anl-surface-elev)] border-[var(--anl-warning)]/40';
    return 'bg-[var(--anl-surface)] border-[var(--anl-border)]';
  };

  const getGridCols = () => {
    if (showSensorData) return 'grid-cols-9';
    return 'grid-cols-8';
  };

  const getNameSpan = () => {
    if (showSensorData) return 'col-span-3';
    return 'col-span-4';
  };

  return (
    <div
      className={`rounded-md border px-3 py-2.5 ${getRankBg(user.rank)} mb-2 transition-all duration-150 hover:shadow-sm`}
    >
      <div className={`grid ${getGridCols()} items-center gap-3`}>
        <div className={`${getNameSpan()} flex items-center space-x-2.5`}>
          <RankIcon rank={user.rank} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-[var(--anl-text)]">{user.name}</div>
          </div>
        </div>

        <div className="col-span-2 text-center">
          <div className="text-lg font-bold text-[var(--anl-text)]">{user.sessions}</div>
          <div className="-mt-0.5 text-xs text-[var(--anl-text-muted)]">Sessions</div>
        </div>

        <div className="col-span-2 text-center">
          <div className="text-sm font-medium text-[var(--anl-text)]">
            {Math.round(user.totalMinutes)}m
          </div>
          <div className="-mt-0.5 text-xs text-[var(--anl-text-muted)]">Total Time</div>
        </div>

        {showSensorData && (
          <div className="col-span-1 text-center">
            <div className="flex items-center justify-center space-x-1">
              <Zap className="h-3.5 w-3.5" style={{ color: 'var(--anl-success)' }} />
              <span className="text-sm font-medium text-[var(--anl-text)]">
                {user.hasSensorData}
              </span>
            </div>
            <div className="-mt-0.5 text-xs text-[var(--anl-text-muted)]">Sensor</div>
          </div>
        )}
      </div>
    </div>
  );
}

export function UserRankingTable({
  data,
  title = 'User Rankings',
  subtitle = 'Ranked by total sessions',
  emptyMessage = 'No user data available',
  emptyDescription = 'Rankings will appear here once users complete sessions',
  showSensorData = true,
  className = '',
}: UserRankingTableProps) {
  if (data.length === 0) {
    return (
      <div className={`space-y-0 ${className}`}>
        {title && (
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center text-base font-semibold text-[var(--anl-text)]">
              <Trophy className="mr-2 h-4 w-4" style={{ color: 'var(--anl-warning)' }} />
              {title}
            </h3>
            {subtitle && <div className="text-xs text-[var(--anl-text-muted)]">{subtitle}</div>}
          </div>
        )}

        <div className="flex flex-col items-center justify-center py-8 text-[var(--anl-text-muted)]">
          <User className="mb-2 h-10 w-10 text-[var(--anl-border)]" />
          <p className="text-base font-medium">{emptyMessage}</p>
          {emptyDescription && <p className="text-xs">{emptyDescription}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-0 ${className}`}>
      {title && (
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center text-base font-semibold text-[var(--anl-text)]">
            <Trophy className="mr-2 h-4 w-4" style={{ color: 'var(--anl-warning)' }} />
            {title}
          </h3>
          {subtitle && <div className="text-xs text-[var(--anl-text-muted)]">{subtitle}</div>}
        </div>
      )}

      {data.map(user => (
        <UserRankingRow key={user.userId} user={user} showSensorData={showSensorData} />
      ))}
    </div>
  );
}

export function processSessionDataToRankings(sessions: any[]): UserRankingData[] {
  const userMap = sessions.reduce(
    (acc, session) => {
      const userName = formatUserName(session);

      if (!acc[userName]) {
        acc[userName] = {
          name: userName,
          userId: session.userId,
          sessions: 0,
          totalMinutes: 0,
          avgDuration: 0,
          hasSensorData: 0,
          rank: 0,
        };
      }

      acc[userName].sessions += 1;
      acc[userName].totalMinutes += session.durationMinutes;
      if (session.hasSensorData) acc[userName].hasSensorData += 1;

      return acc;
    },
    {} as Record<string, UserRankingData>,
  );

  const userRankings: UserRankingData[] = (Object.values(userMap) as UserRankingData[])
    .map((user: UserRankingData) => ({
      ...user,
      avgDuration: Math.round(user.totalMinutes / user.sessions),
    }))
    .sort((a, b) => b.sessions - a.sessions)
    .map((user, index) => ({ ...user, rank: index + 1 }));

  return userRankings;
}

export default UserRankingTable;
